import { createConsumer, Subscription } from '@rails/actioncable'
import type {
  EscalationData,
  ResolutionData,
  ToolCallStatus,
} from 'api/recoveryAi.types'
import type {
  ChatMessage,
  RecoveryChatSource,
} from 'hooks/websocket/recoveryChatSource'
import { useCallback, useEffect, useRef, useState } from 'react'

interface InboundMessage {
  type: string
  content?: string
  chat_id?: string
  message?: string
  code?: string
}

// Surface protocol drift instead of swallowing it — a partial deploy or
// schema change on the backend would otherwise produce a chat with no
// resolution/escalation and no visible error.
//
// TODO: route through a structured logger (Sentry, Datadog) once one is
// wired up in this app. Until then, console.error is the only signal we
// have in production.
const logChannelParseError = (
  kind: string,
  err: unknown,
  raw: string
): void => {
  // eslint-disable-next-line no-console
  console.error(
    `[useRecoveryChat] Failed to parse ${kind} payload`,
    err,
    raw.slice(0, 500)
  )
}

export const useRecoveryChat: RecoveryChatSource = ({
  token,
  merchantId,
  scenario,
  conversationContext,
  enabled = true,
}) => {
  // Session nonce prevents React key reuse after reset() — without this,
  // the first message of session N would collide with the first of N-1.
  const sessionRef = useRef(0)
  const messageCounterRef = useRef(0)
  const nextMessageId = () => {
    messageCounterRef.current += 1
    return `recovery-msg-${sessionRef.current}-${messageCounterRef.current}`
  }

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState<
    string | null
  >(null)
  const streamingRef = useRef<string | null>(null)
  const [resolutionData, setResolutionData] = useState<ResolutionData | null>(
    null
  )
  const [escalationData, setEscalationData] = useState<EscalationData | null>(
    null
  )
  const [isToolRunning, setIsToolRunning] = useState(false)
  const [awaitingResponse, setAwaitingResponse] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const chatIdRef = useRef<string | null>(null)
  const isFirstMessageRef = useRef(true)
  // Starts true: the first turn of any subscription opens a conversation. Sent on EVERY
  // message, not just reset turns — an explicit true/false is what tells the backend to stop
  // inferring a reset from the payload shape, and a client that sends it only sometimes is
  // still inferred about on the turns that omit it.
  const newConversationRef = useRef(true)
  // Conversations we have walked away from. Remembering them is what makes the inbound filter
  // work in the window it exists for: reset() nulls chatIdRef, and nothing re-populates it until
  // the NEXT message makes the backend mint and announce a chat — so a filter keyed only on
  // "differs from the current id" is inert for exactly that span.
  const supersededChatIdsRef = useRef<Set<string>>(new Set())
  const subscriptionRef = useRef<Subscription | null>(null)
  const consumerRef = useRef<ReturnType<typeof createConsumer> | null>(null)
  // Read conversationContext from a ref so the subscription effect's stable
  // deps don't re-create the channel every time the context changes.
  const conversationContextRef = useRef(conversationContext)
  conversationContextRef.current = conversationContext

  // Track only the scenario id in deps — the full scenario object is read
  // via conversationContextRef so we don't want to re-subscribe on every
  // object-identity change, and pulling the id out lets exhaustive-deps
  // verify the effect without a suppression comment.
  const scenarioId = scenario?.scenario_id ?? null

  // Bumped by `restart` to force the subscription effect to tear down and
  // re-subscribe when nothing else in its deps changed. A scenario change already
  // does this implicitly (scenarioId moves); this is the same lever for a caller
  // that wants a brand-new conversation on the SAME scenario.
  //
  //   context: every existing caller leaves this at 0 forever, so the effect runs
  //   exactly when it did before — this widens the deps without changing any
  //   current behaviour.
  const [resubscribeNonce, setResubscribeNonce] = useState(0)

  // Connect to ActionCable RecoveryChatChannel. Requires token + merchant and
  // caller readiness (`enabled`): the channel's `subscribed` needs neither a
  // scenario nor a conversation context, so the free-chat Agent Tuning sandbox
  // (scenario === null) still subscribes — but only once its shell is ready, so
  // a loading/errored/configure-gated merchant opens no background socket.
  // scenarioId stays in the deps so the wizard re-subscribes (a fresh backend
  // chat) whenever the merchant switches persona.
  useEffect(() => {
    if (!enabled || !token || !merchantId) return

    const apiUrl = process.env.REACT_APP_API_URL
    if (!apiUrl) return

    // Refuse to connect over plaintext outside local dev. Without this, a
    // misconfigured `http://` REACT_APP_API_URL would silently send the
    // OAuth token and chat traffic in clear text via ws://.
    const isLocalDev =
      apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')
    if (!isLocalDev && !apiUrl.startsWith('https://')) {
      setConnectionError(
        'Refusing to open chat over unencrypted connection. Set REACT_APP_API_URL to an https:// origin.'
      )
      return
    }

    const wsUrl = apiUrl
      .replace(/\/$/, '')
      .replace('https://', 'wss://')
      .replace('http://', 'ws://')

    const consumer = createConsumer(`${wsUrl}/cable`)
    consumerRef.current = consumer
    // Captured for the cleanup: reading .current there would race the next effect run.
    const supersededChatIds = supersededChatIdsRef.current

    // TODO(rai-pilot): Before the `recovery-ai` LD flag flips on for any
    // merchant, verify this channel's wire contract end-to-end against the
    // backend `RecoveryChatChannel`. Mismatches stall chats silently rather
    // than failing loudly, so the verification step matters more than usual.
    // Full checklist (inbound types, outbound `_json` strings, enum renames)
    // in ~/shaakwave/projects/rai-pilot/docs/deferred-fixes.md
    // under "Pre-flag-flip verification".
    const subscription = consumer.subscriptions.create(
      {
        channel: 'RecoveryChatChannel',
        token,
        merchant_id: merchantId,
      },
      {
        connected() {
          setIsConnected(true)
          setConnectionError(null)
        },

        disconnected() {
          setIsConnected(false)
          // A dropped socket won't deliver an in-flight reply, so un-hang the
          // turn. Otherwise awaitingResponse stays true with the compose box
          // disabled AND (with the mid-stream reset guard) the reset control
          // hidden — no in-app recovery short of a page refresh.
          setAwaitingResponse(false)
        },

        rejected() {
          setIsConnected(false)
          setAwaitingResponse(false)
          setConnectionError(
            'Connection rejected — authentication may have failed.'
          )
        },

        received(data: InboundMessage) {
          // Drop a turn the backend has already superseded. A reset sends nothing on the
          // wire, so the server only learns about it on our next message — until then the
          // abandoned turn keeps streaming, and its frames would clear this turn's
          // awaiting state and append its answer to the fresh transcript.
          //
          // connection_established is exempt because it is the only frame we adopt an id
          // from, and error is exempt because rate-limit and size frames are stamped with
          // the conversation the server has open, not with the turn that provoked them.
          if (
            data.type !== 'connection_established' &&
            data.type !== 'error' &&
            data.chat_id &&
            (supersededChatIdsRef.current.has(data.chat_id) ||
              (chatIdRef.current && data.chat_id !== chatIdRef.current))
          ) {
            return
          }

          switch (data.type) {
            // The ONLY place chat_id is adopted. Taking one off a chunk would pin us back
            // onto a conversation we just abandoned, defeating both the filter above and
            // the new_conversation flag.
            case 'connection_established':
              if (data.chat_id) {
                chatIdRef.current = data.chat_id
              }
              break

            case 'text_chunk':
              setAwaitingResponse(false)
              if (data.content) {
                const chunk = data.content
                streamingRef.current = (streamingRef.current || '') + chunk
                setCurrentStreamingMessage(streamingRef.current)
              }
              break

            case 'message_complete': {
              setIsToolRunning(false)
              setAwaitingResponse(false)
              const completed = streamingRef.current
              streamingRef.current = null
              setCurrentStreamingMessage(null)
              if (completed) {
                // Compute the id BEFORE the updater. nextMessageId mutates
                // messageCounterRef as a side effect; React StrictMode
                // double-invokes state updater functions to check purity,
                // which would call nextMessageId twice and skip an id.
                // (Same pattern enforced by conventions.md "Never nest
                // state updates inside state updater functions".)
                const id = nextMessageId()
                setMessages(msgs => [
                  ...msgs,
                  { id, role: 'ai', content: completed },
                ])
              }
              break
            }

            case 'resolution_data':
              setIsToolRunning(false)
              if (data.content) {
                try {
                  setResolutionData(JSON.parse(data.content) as ResolutionData)
                } catch (err) {
                  logChannelParseError('resolution_data', err, data.content)
                  setConnectionError(
                    'Received malformed resolution data from server.'
                  )
                }
              }
              break

            case 'escalation_data':
              setIsToolRunning(false)
              if (data.content) {
                try {
                  setEscalationData(JSON.parse(data.content) as EscalationData)
                } catch (err) {
                  logChannelParseError('escalation_data', err, data.content)
                  setConnectionError(
                    'Received malformed escalation data from server.'
                  )
                }
              }
              break

            case 'tool_call':
              if (data.content) {
                try {
                  const status = JSON.parse(data.content) as ToolCallStatus
                  setIsToolRunning(status.status === 'started')
                } catch (err) {
                  logChannelParseError('tool_call', err, data.content)
                  // Clear the indicator so the UI doesn't get stuck mid-tool.
                  setIsToolRunning(false)
                }
              }
              break

            case 'error':
              setAwaitingResponse(false)
              // Clear the indicator so the UI doesn't get stuck mid-tool — an
              // error arriving while a tool call is in progress would otherwise
              // leave isToolRunning true forever (nothing else clears it short
              // of a full reset()), pinning ChatPanel's "thinking" indicator on
              // alongside the error banner.
              setIsToolRunning(false)
              setConnectionError(data.content || 'Unknown error')
              break
          }
        },
      }
    )

    subscriptionRef.current = subscription

    return () => {
      subscription.unsubscribe()
      consumer.disconnect()
      subscriptionRef.current = null
      consumerRef.current = null
      // Session boundary: the next subscription is a brand-new backend chat,
      // so any chat_id / first-message flag from the old session is stale.
      // Without this, a token refresh or merchant switch would send the old
      // chat_id and skip conversation_context on the next outbound message.
      // Also reset isConnected so a kickoff effect waits for the new
      // `connected()` callback rather than firing against the stale value.
      chatIdRef.current = null
      isFirstMessageRef.current = true
      newConversationRef.current = true
      // A new socket is a new server-side channel instance, so the old ids can never recur.
      supersededChatIds.clear()
      streamingRef.current = null
      messageCounterRef.current = 0
      setIsConnected(false)
    }
  }, [token, merchantId, scenarioId, enabled, resubscribeNonce])

  // Build outbound payload. Channel expects approved_rewards_json and
  // order_context_json as serialized JSON strings. Reads from ref to
  // avoid stale closure over conversationContext.
  const buildOutboundPayload = (
    message: string,
    extra: Record<string, unknown> = {}
  ): Record<string, unknown> => {
    const ctx = conversationContextRef.current
    const payload: Record<string, unknown> = {
      message,
      chat_id: chatIdRef.current,
      new_conversation: newConversationRef.current,
      ...extra,
    }
    // Cleared synchronously here, like isFirstMessageRef below, so the flag describes this
    // turn only — the backend answers a true by minting a chat and announcing it.
    newConversationRef.current = false

    // Only flip the first-message flag if we actually attached context.
    // Otherwise a future caller that sends before `conversationContext` is
    // built would permanently lose the backend handshake — every subsequent
    // message would also skip context because the flag is already false.
    if (isFirstMessageRef.current && ctx) {
      payload.conversation_context = {
        brand_voice_text: ctx.brand_voice_text,
        philosophy_posture: ctx.philosophy_posture,
        approved_rewards_json: JSON.stringify(ctx.approved_rewards),
        daily_budget_cents: ctx.daily_budget_cents,
        // `?? {}` is defence, not a live path: free chat now anchors on
        // getFreeChatOrder(), so every caller supplies an order. Kept because
        // `JSON.stringify(null)` → "null", which the brain JSON.parses to nil and
        // then dereferences → crash (BUG-214).
        order_context_json: JSON.stringify(ctx.order_context ?? {}),
        sandbox_prompt_text: ctx.sandbox_prompt_text,
        // Empty string, not "null", when there is no dossier (free chat /
        // Agent Tuning) — thanx-ai treats an absent/empty dossier as every
        // signal Unknown, same contract as the other *_json fields.
        dossier_json: ctx.dossier ? JSON.stringify(ctx.dossier) : '',
      }
      isFirstMessageRef.current = false
    }

    return payload
  }

  const sendMessage = useCallback(
    (text: string) => {
      // Bail before any state mutation if the subscription isn't ready —
      // otherwise the user message would render and `awaitingResponse`
      // would flip to true with no way to clear it.
      if (!subscriptionRef.current) return

      // Compute the id OUTSIDE the updater (StrictMode double-invokes
      // updaters and nextMessageId mutates messageCounterRef).
      const id = nextMessageId()
      // Keep this optimistic append BEFORE `setAwaitingResponse` — the socket
      // never echoes the user's own message back, so this local entry is the
      // only thing that marks the turn as sent-but-unanswered.
      // `useCannedConversation.agentHasReplied` reads the last message's role to
      // decide whether the scripted customer may speak again; reordering these
      // two would let it fire a second scripted turn against the previous
      // agent reply. Its tests mock this function out, so they will not catch it.
      setMessages(prev => [...prev, { id, role: 'user', content: text }])
      setAwaitingResponse(true)
      subscriptionRef.current.send(buildOutboundPayload(text))
    },
    [] // stable — reads from refs, no closure deps
  )

  const reset = useCallback(() => {
    setMessages([])
    setCurrentStreamingMessage(null)
    streamingRef.current = null
    setResolutionData(null)
    setEscalationData(null)
    setIsToolRunning(false)
    setAwaitingResponse(false)
    setConnectionError(null)
    // Drop isConnected so a consumer whose reset() is paired with a re-subscribe
    // (the wizard's persona switch changes scenarioId) waits for the NEW socket's
    // connected() before auto-sending. Without this the opener fires against the
    // freshly-created, not-yet-open socket and ActionCable silently drops it —
    // stuck ellipsis, no reply. A same-socket reset (the tab's "New conversation")
    // never re-subscribes and doesn't read isConnected, so this is inert there.
    setIsConnected(false)
    if (chatIdRef.current) {
      supersededChatIdsRef.current.add(chatIdRef.current)
    }
    chatIdRef.current = null
    isFirstMessageRef.current = true
    // Tell the backend explicitly, rather than leaving it to infer a reset from the
    // null chat_id and the re-sent context.
    newConversationRef.current = true
    messageCounterRef.current = 0
    sessionRef.current += 1
  }, [])

  // reset() + a forced re-subscribe. See RecoveryChatSourceResult#restart for why
  // the pair is indivisible: reset() leaves isConnected false, and only a new
  // subscription's connected() callback raises it again.
  //
  // Ordering is deliberate — reset() first so the outgoing chat id is added to
  // supersededChatIds and the log is already empty before the teardown runs. The
  // teardown then clears that set, which is correct: a new socket is a new
  // server-side channel instance, so the old ids can never recur.
  const restart = useCallback(() => {
    reset()
    setResubscribeNonce(nonce => nonce + 1)
  }, [reset])

  return {
    messages,
    currentStreamingMessage,
    resolutionData,
    escalationData,
    isToolRunning,
    awaitingResponse,
    isConnected,
    connectionError,
    sendMessage,
    reset,
    restart,
  }
}
